<template>
    <div>
        <Dialog :visible.sync="exportDialog" :style="{width: '300px'}" header="Export" :modal="true"
                :maximizable="true">
            <div class="p-field">
                <div v-for="category of categories" :key="category.key" class="p-field-checkbox">
                    <Checkbox :id="category.key" name="category" :value="category" v-model="selectedCategories"
                              :class="{'p-invalid': submitted && !selectedCategories.length}"/>
                    <label :for="category.name">{{ category.name }}</label>
                </div>
                <small class="p-invalid" v-if="submitted && !selectedCategories.length">Please choose at least one
                    box.</small>
                <Button label="Select All" class="p-button-outlined p-button-success p-m-2" @click="selectAll"/>
                <Button label="Clear All" class="p-button-outlined p-button-danger p-m-2" @click="clearAll"/>
            </div>

            <template #footer>
                <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="hideDialog"/>
                <Button label="Export" icon="pi pi-check" class="p-button-text" @click="download"/>
            </template>
        </Dialog>
    </div>
</template>

<script>
import {Component, Prop, Vue, Watch} from "vue-property-decorator";

import Toast from 'primevue/toast';
import Dialog from "primevue/dialog";
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import store from "../../store";
import StaffService from "@/service/StaffService";

/**
 * Class-component
 */
@Component({
    components: {
        Toast: Vue.extend(Toast),
        Dialog: Vue.extend(Dialog),
        Checkbox: Vue.extend(Checkbox),
        Button: Vue.extend(Button),
    }
})
export default class ExportDialog extends Vue {
    staffService = new StaffService();
    exportDialog = false;
    categories = [{
        key: 1,
        name: 'Academics'
    }, {
        key: 2,
        name: 'Professionals'
    }, {
        key: 3,
        name: 'Trainings'
    }];
    selectedCategories = this.categories
    submitted = false;
    loading = false;
    toastSuccess = {
        severity: 'success',
        summary: 'Successful',
        detail: '',
        life: 3000
    };
    toastError = {
        severity: 'error',
        summary: 'Error',
        detail: '',
        life: 3000
    };

    @Watch('selectedCategories')
    onSelectedCategoriesChanged() {
        this.submitted = false
    }

    selectAll() {
        this.selectedCategories = this.categories
    }

    clearAll() {
        this.selectedCategories = []
    }

    async download() {
        try {
            this.loading = true;
            this.submitted = true;
            if (!this.selectedCategories.length) return // invalid

            if (Object.keys(store.state.user).length && store.state.staffId) {
                const relArr = []
                for (const selectedCategory of this.selectedCategories) {
                    if (selectedCategory.name === 'Academics') relArr.push('academics')
                    if (selectedCategory.name === 'Professionals') relArr.push('professionals', 'professionals.professional')
                    if (selectedCategory.name === 'Trainings') relArr.push('trainings')
                }
                const staff = await this.staffService.getStaffRelationships(store.state.staffId, relArr)

                // construct data array to be converted to csv
                const data = []
                data.push({'a': 'FHK QTD Summary', 'b': '', 'c': '', 'd': '', 'e': '', 'f': '', 'g': ''}) // header

                // user data from Graph API
                data.push({'a': 'Staff Name', 'b': store.state.user.displayName}) // row: staff name
                data.push({'a': 'Job Title', 'b': store.state.user.jobTitle}) // row: staff title

                // academics
                if (relArr.includes('academics')) {
                    data.push({'a': 'Academics'}) // row: academics header
                    const academics = Object.values(staff.academics)
                    if (academics.length) {
                        data.push({
                            'a': '',
                            'b': 'Level',
                            'c': 'Discipline',
                            'd': 'Title',
                            'e': 'Description',
                            'f': 'Institution',
                            'g': 'Awarded Month'
                        }) // row: academics fields
                        let count = 1
                        for (const academic of academics) {
                            data.push({
                                'a': count,
                                'b': academic.level,
                                'c': academic.discipline,
                                'd': academic.title,
                                'e': academic.description,
                                'f': academic.institution,
                                'g': this.$formatDate(academic.date)
                            }) // row: academics data
                            count++
                        }
                    } else {
                        data.push({'a': 'No Records'})
                    }
                }

                // professionals
                if (relArr.includes('professionals')) {
                    data.push({'a': 'Professionals'}) // row: professionals header
                    const professionals = Object.values(staff.professionals)
                    if (professionals.length) {
                        data.push({
                            'a': '',
                            'b': 'Title',
                            'c': 'Full Title',
                            'd': 'Institution',
                            'e': 'Description',
                            'f': 'Awarded Month'
                        }) // row: professionals fields
                        let count = 1
                        for (const professional of professionals) {
                            data.push({
                                'a': count,
                                'b': professional.professional.title,
                                'c': professional.professional.description,
                                'd': professional.professional.institution,
                                'e': professional.description,
                                'f': this.$formatDate(professional.date)
                            }) // row: professionals data
                            count++
                        }
                    } else {
                        data.push({'a': 'No Records'})
                    }
                }

                // trainings
                if (relArr.includes('trainings')) {
                    data.push({'a': 'Trainings'}) // row: trainings header
                    const trainings = Object.values(staff.trainings)
                    if (trainings.length) {
                        data.push({
                            'a': '',
                            'b': 'Category',
                            'c': 'Name',
                            'd': 'Description',
                            'e': 'Organizer',
                            'f': 'Awarded Month'
                        }) // row: trainings fields
                        let count = 1
                        for (const training of trainings) {
                            data.push({
                                'a': count,
                                'b': training.category,
                                'c': training.name,
                                'd': training.description,
                                'e': training.organizer,
                                'f': this.$formatDate(training.date)
                            }) // row: trainings data
                            count++
                        }
                    } else {
                        data.push({'a': 'No Records'})
                    }
                }

                const csvString = this.$papaparse.unparse(data, {header: false})
                this.$exportCsv(csvString, `FHK-QTD-Summary-${this.$moment().format('YYYY_MM_DD-HH_mm_ss')}`)
            }

            this.toastSuccess.detail = 'Export File Downloaded'
            this.$toast.add(this.toastSuccess);
            this.loading = false;
        } catch (e) {
            this.toastError.detail = e.message
            this.toastError.life = null
            this.$toast.add(this.toastError);
            this.staffService.logError(e).forEach(err => {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Details',
                    detail: err,
                    life: null
                });
            })
        }
        this.exportDialog = false
    }

    hideDialog() {
        this.exportDialog = false;
        this.submitted = false;
    }

    logError(e) {
        const error = []
        if (e.response.data.errors) {
            // Work with each error from the JSONAPI 'errors' array
            for (const err of e.response.data.errors) {
                error.push(err.detail)
            }
        }
        return error
    }
}


</script>

<style scoped>

</style>